import { assertUncomputedFromBase, type FromBaseRow } from '../janus-engine/compile/from-base-context';
import { entityRuntimeMetadata } from '../janus-engine/compile/entity-runtime-metadata';
import { createNpcDefinitionFinalizer } from '../janus-engine/compile/from-base-npc-finalization';
import { inventoryCatalogScope, mergeInventoryMetadata, type InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { readRandomTableDef } from '../janus-engine/rolls/random-table';
import type { RandomTableData } from './render-table-tag';

export interface RandomTableTagInputs extends Omit<RandomTableData, 'die' | 'authored_roll_pools' | 'schema' | 'format'> {
    header_row: FromBaseRow;
    definition_inputs: { base: FromBaseRow; shared: InventorySharedCatalog };
}

/** Stateless native definition hydration also works on anonymous hosts without a websocket owner. */
export function resolveRandomTableTagInputs(data: RandomTableTagInputs): Omit<RandomTableData, 'format'> {
    const { definition_inputs: { base: selected, shared }, header_row: header, ...presentation } = data;
    assertUncomputedFromBase(selected);
    assertUncomputedFromBase(shared);
    const base = inventoryCatalogScope(shared, selected).base;
    const metadata = entityRuntimeMetadata(shared.metadata as Parameters<typeof entityRuntimeMetadata>[0]);
    const inventory = mergeInventoryMetadata(metadata.inventory, base.inventory_metadata);
    const definition = createNpcDefinitionFinalizer(base, metadata.player, inventory).definition(data.item_id, []);
    // The definition finalizer interns range buckets on references, not the root. This projection
    // needs only the native pool reader; presentation keeps the endpoint's saved range ordering.
    const table = readRandomTableDef(data.item_id, { ...definition, random_table_ranges: [] });
    const effect = String(readItemField(header, 'roll_result_effect') ?? '') !== ''
        || String(readItemField(header, 'item_id_attached') ?? '') !== '';
    const description = String(readItemField(header, 'roll_result_effect_description') ?? '') !== '';
    return { ...presentation, die: (readItemField(header, 'roll_range_end') ?? '') as number | string,
        authored_roll_pools: table?.pools ?? [], schema: effect ? (description ? 1 : 2) : (description ? 3 : 0) };
}
